import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useTranslations, useLanguage } from "@vocab/react";
import { Box, ContentBlock, Stack, Loader, Dialog, Columns, Column, Text, useThemeName, Heading, Card, Divider, Bleed, Badge, List, Alert, Inline } from "braid-design-system";
import { translations as translations$1 } from "./components/ErrorAlert/.vocab/index.mjs";
import { ErrorAlert } from "./styles/components/ErrorAlert/ErrorAlert.mjs";
import { useContractBalancesForHirerBalanceQuery, useHirerAccountForHirerBalanceQuery, useOrderSummaryDetailsQuery, useGetOrderSummaryQuery, useGetContractBalancesQuery, useHirerAccountQuery } from "./graphql-types.chunk.mjs";
import * as React from "react";
import { useContext, useEffect, createContext, Fragment as Fragment$1, useRef, useCallback, useState } from "react";
import { HirerBalanceContext, useTracking, HirerBalanceContextProvider, AdBudgetsUpsell, TrackingProvider } from "./styles/HirerContractBalance/components/AdBudgetsUpsell/AdBudgetsUpsell.mjs";
import { BalanceCarousel } from "./styles/HirerContractBalance/components/BalanceCarousel/BalanceCarousel.mjs";
import { formatMinorUnits } from "@seek/currency";
import { Remaining, formatExpiryDate, PermissionsProvider } from "./styles/HirerContractBalance/components/BalanceCarousel/BalanceCarouselWrapper.mjs";
import { translations } from "./HirerContractBalance/components/Contract/.vocab/index.mjs";
import { translations as translations$2 } from "./HirerOrderSummary/components/.vocab/index.mjs";
import { OrderTableRow } from "./styles/HirerOrderSummary/components/OrderTableRow/OrderTableRow.mjs";
import { TaxSuffix } from "@seek/tax-suffix";
import { format, parseISO } from "date-fns";
import { SubTotal } from "./styles/HirerOrderSummary/components/SubTotal/SubTotal.mjs";
import { getSupportedPaymentMethodsForCaja, PaymentsForCaja } from "@seek/payments-widget";
import { checkIsPaymentsReturnUrl } from "@seek/payments-widget";
import { ShoppingCartContext } from "@seek/shopping-cart-provider";
import { HirerPromoCodesBanner, usePromoCodesBanner } from "@seek/hirer-promo-codes-banner";
import { HirerPromoCodesInput } from "@seek/hirer-promo-codes-input";
import { locales } from "@seek/melways-sites";
import { translations as translations$3 } from "./Checkout/.vocab/index.mjs";
const LoadingBlock = ({
  size,
  delayVisibility = false
}) => /* @__PURE__ */ jsx(Box, { background: "surface", role: "loading", children: /* @__PURE__ */ jsx(ContentBlock, { children: /* @__PURE__ */ jsx(Box, { paddingY: ["xlarge", "xxlarge"], children: /* @__PURE__ */ jsx(Stack, { space: "xlarge", align: "center", children: /* @__PURE__ */ jsx(Loader, { size, delayVisibility }) }) }) }) });
const ContractBalanceBreakdownDialog = ({
  contractBalance,
  warningType
}) => {
  const { calloutVisible, hideCallout } = useContext(HirerBalanceContext);
  const { t } = useTranslations(translations);
  const { contracts, dollarBased, title } = contractBalance;
  const activeContracts = (contracts == null ? void 0 : contracts.filter((x) => x.current)) ?? [];
  const stackedContracts = (contracts == null ? void 0 : contracts.filter((x) => !x.current)) ?? [];
  return /* @__PURE__ */ jsxs(
    Dialog,
    {
      id: "cbb-dialog",
      title: t("Balance breakdown"),
      open: calloutVisible,
      onClose: () => hideCallout(),
      width: "small",
      children: [
        activeContracts.map((contract, index) => /* @__PURE__ */ jsx(
          ContractComponent,
          {
            contract,
            warningType,
            isDollarValue: dollarBased,
            label: title
          },
          index
        )),
        stackedContracts.map((contract, index) => /* @__PURE__ */ jsx(
          ContractComponent,
          {
            contract,
            warningType: "NoWarning",
            isDollarValue: dollarBased,
            label: title,
            isStacked: true
          },
          index
        ))
      ]
    }
  );
};
const ContractComponent = ({
  contract,
  warningType,
  isDollarValue,
  label,
  isStacked
}) => {
  var _a;
  const { locale } = useContext(HirerBalanceContext);
  const { t } = useTranslations(translations);
  const {
    expiry: { expiryDate },
    value
  } = contract;
  const remainingValue = isDollarValue ? value.dollars.remaining.value ?? 0 : ((_a = value.units) == null ? void 0 : _a.remaining) ?? 0;
  const isCompactPrice = remainingValue.toString().length >= 9;
  const currency = value.dollars.remaining.currency;
  const hasValidExpiryDate = isStacked ? false : !expiryDate.startsWith("9999");
  return /* @__PURE__ */ jsxs(Columns, { space: "large", collapseBelow: "desktop", children: [
    /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(
      Remaining,
      {
        totalValue: value,
        isDollarValue,
        warningType
      }
    ) }),
    /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Box, { display: "flex", alignItems: "center", height: "full", children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
      /* @__PURE__ */ jsx(Text, { size: "large", weight: "strong", children: label }),
      /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
        isCompactPrice && /* @__PURE__ */ jsxs(Text, { children: [
          formatMinorUnits(Math.floor(remainingValue), {
            currency,
            locale,
            dropFraction: true
          }),
          t("remaining")
        ] }),
        /* @__PURE__ */ jsx(Text, { children: hasValidExpiryDate ? t("Expires date", {
          date: formatExpiryDate(expiryDate)
        }) : t("Not started") })
      ] })
    ] }) }) })
  ] });
};
const HirerBalance = ({ contractBalances }) => {
  const hirerBalanceContext = useContext(HirerBalanceContext);
  const {
    calloutVisible,
    contractBalance: calloutContractBalance,
    warningType: calloutWarningType,
    advertiserDetails
  } = hirerBalanceContext;
  const noContracts = contractBalances.length === 0;
  if (noContracts || advertiserDetails.hideBalance) {
    return /* @__PURE__ */ jsx(AdBudgetsUpsell, {});
  }
  return /* @__PURE__ */ jsxs(Box, { position: "relative", background: "surface", children: [
    calloutVisible && calloutContractBalance && /* @__PURE__ */ jsx(
      ContractBalanceBreakdownDialog,
      {
        contractBalance: calloutContractBalance,
        warningType: calloutWarningType
      }
    ),
    /* @__PURE__ */ jsx(BalanceCarousel, { balances: contractBalances })
  ] });
};
const HirerBalancePresentation = (props) => {
  const { contractBalances, pricingModel, site, language, isStaging, locale } = props;
  const hideBalance = contractBalances.length > 0 ? contractBalances.every((balance) => balance.hideBalance) : false;
  const sortedContractBalances = contractBalances.filter((c) => c.dollarBased).concat(contractBalances.filter((c) => !c.dollarBased));
  const advertiserDetails = {
    contractBalances: sortedContractBalances,
    pricingModel,
    hideBalance
  };
  const { onImpression } = useTracking();
  useEffect(() => {
    onImpression({ contractBalances, hideBalance, pricingModel });
  }, [contractBalances, pricingModel, hideBalance, onImpression]);
  return /* @__PURE__ */ jsx(
    HirerBalanceContextProvider,
    {
      advertiserDetails,
      site,
      language,
      isStaging,
      locale,
      children: /* @__PURE__ */ jsx(HirerBalance, { contractBalances: sortedContractBalances })
    }
  );
};
const mapGraphqlToContractBalanceProp = (contractBalance) => {
  const currentContract = (contractBalance.contracts || [])[0];
  return {
    ...contractBalance,
    expiresSoon: currentContract == null ? void 0 : currentContract.expiry.expiresSoon
  };
};
const getPricingModel = (pricingModelFromHirerAccount, hirerAccountError) => {
  if (hirerAccountError || !pricingModelFromHirerAccount) {
    return "HORIZON";
  }
  return pricingModelFromHirerAccount;
};
const HirerContractBalance = ({
  locale,
  advertiserNation,
  canCreateJobs,
  tracking = () => {
  },
  ...otherProps
}) => {
  var _a;
  const {
    loading: contractBalanceLoading,
    error: contractBalanceError,
    data: contractBalanceData
  } = useContractBalancesForHirerBalanceQuery({
    variables: { advertiserNation }
  });
  const {
    loading: hirerAccountLoading,
    error: hirerAccountError,
    data: hirerAccountData
  } = useHirerAccountForHirerBalanceQuery();
  const { t } = useTranslations(translations$1);
  if (contractBalanceLoading || hirerAccountLoading) {
    return /* @__PURE__ */ jsx(Box, { "data-testid": "Loader", children: /* @__PURE__ */ jsx(LoadingBlock, {}) });
  }
  if (contractBalanceError) {
    return /* @__PURE__ */ jsx(
      ErrorAlert,
      {
        errorMessage: t(
          "A technical error has occurred and we are unable to show your SEEK contract information."
        )
      }
    );
  }
  const pricingModel = getPricingModel(
    (_a = hirerAccountData == null ? void 0 : hirerAccountData.hirerAccount) == null ? void 0 : _a.pricingModel,
    hirerAccountError
  );
  const contractBalances = ((contractBalanceData == null ? void 0 : contractBalanceData.contractBalances) ?? []).map(
    (x) => mapGraphqlToContractBalanceProp(x)
  );
  return /* @__PURE__ */ jsx(TrackingProvider, { context: tracking, children: /* @__PURE__ */ jsx(PermissionsProvider, { canCreateJobs: canCreateJobs ?? false, children: /* @__PURE__ */ jsx(
    HirerBalancePresentation,
    {
      ...otherProps,
      contractBalances,
      pricingModel,
      locale
    }
  ) }) });
};
var Locales = /* @__PURE__ */ ((Locales2) => {
  Locales2["AU"] = "AU";
  Locales2["NZ"] = "NZ";
  return Locales2;
})(Locales || {});
const defaultLogger$1 = {
  /* eslint-disable no-console */
  warn: console.warn,
  error: console.error,
  info: console.info
  /* eslint-enable no-console */
};
const LoggerContext = React.createContext(defaultLogger$1);
const LoggerProvider = ({ children, context }) => /* @__PURE__ */ jsx(
  LoggerContext.Provider,
  {
    value: {
      ...defaultLogger$1,
      ...context
    },
    children
  }
);
const useLogger = () => {
  const { warn, error, info } = useContext(LoggerContext);
  return { warn, error, info };
};
const OrderSummaryLayout = ({
  children,
  paddingStyles,
  title,
  prefixContent
}) => {
  const theme = useThemeName();
  return /* @__PURE__ */ jsx(Box, { background: "surface", ...paddingStyles, children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
    Boolean(title) ? /* @__PURE__ */ jsx(Heading, { level: theme === "apac" ? "3" : "4", weight: "regular", children: title }) : null,
    prefixContent,
    theme === "apac" ? /* @__PURE__ */ jsx(Box, { children }) : /* @__PURE__ */ jsx(Card, { children })
  ] }) });
};
const PayableHeader = () => {
  const { t } = useTranslations(translations$2);
  return /* @__PURE__ */ jsx(
    OrderTableRow,
    {
      left: /* @__PURE__ */ jsx(Text, { weight: "strong", align: "left", children: t("items...") }),
      right: /* @__PURE__ */ jsx(Text, { weight: "strong", align: "right", children: t("cost...") })
    }
  );
};
const Total = ({ total, tax, locale, currency }) => {
  const { t } = useTranslations(translations$2);
  const theme = useThemeName();
  return /* @__PURE__ */ jsx(
    OrderTableRow,
    {
      left: tax ? /* @__PURE__ */ jsx(
        TaxSuffix,
        {
          taxCode: tax.code,
          displayRecommendation: "inclusive",
          locale,
          children: /* @__PURE__ */ jsx(Heading, { level: theme === "apac" ? "3" : "4", children: t("total...") })
        }
      ) : /* @__PURE__ */ jsx(Heading, { level: theme === "apac" ? "3" : "4", children: t("total...") }),
      right: /* @__PURE__ */ jsx(Heading, { level: theme === "apac" ? "3" : "4", align: "right", children: formatMinorUnits(total, { currency, locale }) })
    }
  );
};
const taxPrefix = {
  inclusive: "incl. ",
  exclusive: "+"
};
const BillableUnitsSummary = ({
  cpqaOrderSummary
}) => {
  const { maxBillableUnits, unitPrice } = cpqaOrderSummary;
  const taxType = unitPrice.tax ? taxPrefix[unitPrice.tax.displayRecommendation] : void 0;
  const taxSuffix = unitPrice.tax ? `${taxType}${unitPrice.tax.code}` : void 0;
  const costPerApplicantPrice = [unitPrice.formattedPrice, taxSuffix].filter(Boolean).join(" ");
  return /* @__PURE__ */ jsx(Bleed, { horizontal: "medium", children: /* @__PURE__ */ jsx(Box, { background: "neutralLight", paddingX: "medium", paddingY: "medium", children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
    /* @__PURE__ */ jsx(Badge, { tone: "promote", children: "Premium feature" }),
    /* @__PURE__ */ jsx(Text, { size: "small", children: "Relevant applications" }),
    /* @__PURE__ */ jsxs(List, { size: "small", space: "xsmall", children: [
      /* @__PURE__ */ jsxs(Text, { children: [
        "Receive at least ",
        maxBillableUnits,
        " relevant applications (that claim to have working rights and 2+ years' experience)"
      ] }),
      /* @__PURE__ */ jsxs(Text, { children: [
        "Pay ",
        costPerApplicantPrice,
        " less for each relevant application short of ",
        maxBillableUnits
      ] })
    ] })
  ] }) }) });
};
const CPQASummary = ({
  padding,
  locale,
  cpqaOrderSummary,
  orderId,
  trackEventHandler
}) => {
  const { t } = useTranslations(translations$2);
  const { total, currency, tax, cappedPrice } = cpqaOrderSummary;
  useEffect(() => {
    trackEventHandler(
      "order_summary_displayed",
      {
        orderId
      },
      "view"
    );
  }, [orderId, trackEventHandler]);
  return /* @__PURE__ */ jsx(OrderSummaryLayout, { paddingStyles: padding, title: t("order summary..."), children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
    /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
      /* @__PURE__ */ jsx(PayableHeader, {}),
      /* @__PURE__ */ jsx(Divider, { weight: "strong" })
    ] }),
    /* @__PURE__ */ jsx(
      OrderTableRow,
      {
        left: /* @__PURE__ */ jsx(Text, { weight: "strong", tone: "secondary", children: "Premium Ad" }),
        right: /* @__PURE__ */ jsx(Text, { weight: "strong", align: "right", tone: "secondary", children: cappedPrice.formattedPrice })
      }
    ),
    cappedPrice.tax && /* @__PURE__ */ jsx(
      OrderTableRow,
      {
        left: /* @__PURE__ */ jsx(Text, { tone: "secondary", children: cappedPrice.tax.code }),
        right: /* @__PURE__ */ jsx(Text, { tone: "secondary", align: "right", children: cappedPrice.tax.formattedPrice })
      }
    ),
    /* @__PURE__ */ jsx(BillableUnitsSummary, { cpqaOrderSummary }),
    /* @__PURE__ */ jsx(Total, { total, tax, locale, currency })
  ] }) });
};
function assert(condition, msg) {
  if (!condition) {
    throw new Error(msg);
  }
}
const AlertMessage = ({ message }) => /* @__PURE__ */ jsx(Alert, { tone: "info", children: /* @__PURE__ */ jsx(Text, { children: message }) });
const ProductLineItemContext = createContext(void 0);
const LineItemCost = ({ lineItem, showMinus, locale }) => {
  const tone = useContext(ProductLineItemContext);
  if (lineItem.state === "fullPrice" && lineItem.netPrice !== lineItem.retailPrice) {
    return /* @__PURE__ */ jsxs(Inline, { space: "small", align: "right", children: [
      /* @__PURE__ */ jsx(Text, { tone, children: /* @__PURE__ */ jsx("s", { children: formatMinorUnits(lineItem.retailPrice, {
        currency: lineItem.currency,
        locale
      }) }) }),
      /* @__PURE__ */ jsxs(Text, { tone, children: [
        showMinus ? `–` : "",
        " ",
        formatMinorUnits(lineItem.netPrice, {
          currency: lineItem.currency,
          locale
        })
      ] })
    ] });
  }
  return /* @__PURE__ */ jsxs(Text, { tone, align: "right", children: [
    showMinus ? `– ` : "",
    formatMinorUnits(
      lineItem.state === "fullPrice" ? lineItem.netPrice : lineItem.outstandingAmount,
      {
        currency: lineItem.currency,
        locale
      }
    )
  ] });
};
const ProductLineItem = ({ lineItem, showMinus, showPartial, tone, locale }) => {
  const { t } = useTranslations(translations$2);
  return /* @__PURE__ */ jsx(ProductLineItemContext.Provider, { value: tone, children: /* @__PURE__ */ jsx(
    OrderTableRow,
    {
      left: /* @__PURE__ */ jsxs(Inline, { space: "small", alignY: "center", children: [
        /* @__PURE__ */ jsx(Text, { tone, children: lineItem.title }),
        lineItem.state === "outstanding" && showPartial && /* @__PURE__ */ jsx(Badge, { tone: "neutral", children: t("partial payment...") })
      ] }),
      right: /* @__PURE__ */ jsx(
        LineItemCost,
        {
          lineItem,
          showMinus,
          locale
        }
      )
    }
  ) });
};
const HeaderText = ({
  content,
  expiryDate
}) => {
  const { t } = useTranslations(translations$2);
  return /* @__PURE__ */ jsx(Inline, { space: "xsmall", alignY: "center", children: /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Text, { weight: "medium", children: content }),
    expiryDate && /* @__PURE__ */ jsx(Badge, { tone: "neutral", children: t("expires...", {
      date: format(parseISO(expiryDate), "dd/MM/yyyy")
    }) })
  ] }) });
};
const LeftAlignedText = (props) => /* @__PURE__ */ jsx(Text, { ...props, align: "left" });
const RightAlignedText = (props) => /* @__PURE__ */ jsx(Text, { ...props, align: "right" });
const ContractBalanceItem = ({ contractBalanceItems, item, locale }) => {
  const { t } = useTranslations(translations$2);
  const isUsingStackedContract = contractBalanceItems.length >= 2;
  const showOutstanding = item.outstandingBalance > 0 && item.remainingBalance === 0;
  const theme = useThemeName();
  return /* @__PURE__ */ jsx(Stack, { space: "large", children: /* @__PURE__ */ jsxs(Fragment, { children: [
    item.alertMessage && /* @__PURE__ */ jsx(AlertMessage, { message: t(item.alertMessage) }),
    /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
      /* @__PURE__ */ jsx(
        OrderTableRow,
        {
          left: /* @__PURE__ */ jsx(
            HeaderText,
            {
              content: item.contractName,
              expiryDate: isUsingStackedContract ? void 0 : item.expiryDate
            }
          ),
          right: /* @__PURE__ */ jsx(RightAlignedText, { weight: "medium", children: t("balance...") })
        }
      ),
      /* @__PURE__ */ jsx(Divider, { weight: "strong" })
    ] }),
    /* @__PURE__ */ jsx(
      OrderTableRow,
      {
        left: /* @__PURE__ */ jsx(LeftAlignedText, { weight: "strong", children: t("starting balance...") }),
        right: /* @__PURE__ */ jsx(RightAlignedText, { weight: "strong", children: formatMinorUnits(item.startingBalance, {
          currency: item.currency,
          locale
        }) })
      }
    ),
    /* @__PURE__ */ jsx(Box, { marginBottom: "xsmall", children: /* @__PURE__ */ jsx(Stack, { space: "large", children: item.lineItems.map((lineItem, index) => /* @__PURE__ */ jsx(
      ProductLineItem,
      {
        lineItem,
        showMinus: true,
        locale
      },
      `lineitem-${index}`
    )) }) }),
    showOutstanding ? /* @__PURE__ */ jsx(
      OrderTableRow,
      {
        left: /* @__PURE__ */ jsx(Heading, { align: "left", level: theme === "apac" ? "3" : "4", children: t("outstanding balance...") }),
        right: /* @__PURE__ */ jsxs(RightAlignedText, { tone: "critical", size: "large", weight: "strong", children: [
          "–",
          " ",
          formatMinorUnits(item.outstandingBalance, {
            currency: item.currency,
            locale
          })
        ] })
      }
    ) : /* @__PURE__ */ jsx(
      OrderTableRow,
      {
        left: /* @__PURE__ */ jsx(Heading, { align: "left", level: theme === "apac" ? "3" : "4", children: t("remaining balance...") }),
        right: /* @__PURE__ */ jsx(RightAlignedText, { size: "large", weight: "strong", children: formatMinorUnits(item.remainingBalance, {
          currency: item.currency,
          locale
        }) })
      }
    )
  ] }) });
};
const ContractBalance = ({
  contractBalanceItems,
  locale,
  padding,
  prefixContent
}) => {
  const { t } = useTranslations(translations$2);
  assert(
    (contractBalanceItems == null ? void 0 : contractBalanceItems.length) > 0,
    "contractBalanceItems shouldn't be undefined or empty"
  );
  return /* @__PURE__ */ jsx(
    OrderSummaryLayout,
    {
      paddingStyles: padding,
      title: t("your ad budget..."),
      prefixContent: prefixContent == null ? void 0 : prefixContent.contractBalance,
      children: /* @__PURE__ */ jsx(Stack, { space: "large", children: /* @__PURE__ */ jsx(Stack, { space: "large", children: contractBalanceItems.map((balanceItem, index) => /* @__PURE__ */ jsxs(Fragment$1, { children: [
        index > 0 ? /* @__PURE__ */ jsx(Divider, {}, "divider") : null,
        /* @__PURE__ */ jsx(
          ContractBalanceItem,
          {
            contractBalanceItems,
            item: balanceItem,
            locale
          }
        )
      ] }, index)) }) })
    }
  );
};
const alertMessageResponses = {
  costThisInvoice: "the cost of this...invoice",
  costTheOutstandingBalance: "the cost of the...outstanding amount",
  weWillDeduct: "we will deduct..."
};
const getAmexSurchargeAmount = (orderSummary, surchargeToApply) => {
  var _a, _b;
  return ((_b = (_a = orderSummary.surcharges.find(
    (surcharge) => surcharge.surchargeType === surchargeToApply
  )) == null ? void 0 : _a.amount) == null ? void 0 : _b.value) ?? 0;
};
const isUpsellProductLineItem = (lineItem) => lineItem.product.productType === "AD_BUDGET";
const isLineItemFullyDrawdown = (lineItem) => {
  var _a;
  return (((_a = lineItem.price.netPrice) == null ? void 0 : _a.value) ?? 0) === lineItem.drawdownItems.reduce((aggr, drawdown) => aggr + drawdown.amount, 0);
};
const getNetPriceFromLineItem = (lineItem) => {
  var _a;
  return ((_a = lineItem.price.netPrice) == null ? void 0 : _a.value) ?? 0;
};
const mapLineItem = (lineItem) => {
  var _a;
  const netPrice = getNetPriceFromLineItem(lineItem);
  function getTitle(item) {
    if (item.product.productType === "JOB_AD_EDIT") {
      return "Ad edits";
    }
    if (item.product.productType === "JOB_AD_UPGRADE") {
      return "Upgrade to StandOut ad";
    }
    return lineItem.product.name ?? "";
  }
  return {
    state: "fullPrice",
    netPrice,
    productType: lineItem.product.productType,
    retailPrice: ((_a = lineItem.price.retailPrice) == null ? void 0 : _a.value) ?? 0,
    title: getTitle(lineItem),
    currency: lineItem.price.retailPrice.currency
  };
};
const getOutstandingAmount = (lineItem, amount) => (lineItem.state === "fullPrice" ? lineItem.netPrice : lineItem.outstandingAmount) - amount;
const getPayableLineItem = (lineItem, amount, contractName) => ({
  title: lineItem.title,
  productType: lineItem.productType,
  state: "outstanding",
  paidContractName: contractName,
  currency: lineItem.currency,
  outstandingAmount: getOutstandingAmount(lineItem, amount)
});
const getKeyFromDrawdown = (drawdown) => drawdown.drawdownContractDetails.contractId ?? drawdown.drawdownContractDetails.expiryDate ?? "";
const mapDrawdownItem = (lineItem, drawdown, isUpsell) => {
  var _a, _b, _c;
  const {
    drawdownContractDetails: {
      remainingValue,
      currentValue,
      expiryDate,
      contractName,
      contractType
    }
  } = drawdown;
  return {
    key: getKeyFromDrawdown(drawdown),
    lineItems: [lineItem],
    contractType,
    contractName: contractName ?? contractType,
    remainingBalance: ((_a = remainingValue == null ? void 0 : remainingValue.dollars) == null ? void 0 : _a.value) ?? 0,
    startingBalance: ((_b = currentValue == null ? void 0 : currentValue.dollars) == null ? void 0 : _b.value) ?? 0,
    currency: ((_c = currentValue == null ? void 0 : currentValue.dollars) == null ? void 0 : _c.currency) ?? "",
    outstandingBalance: 0,
    ...expiryDate && { expiryDate },
    ...isUpsell && {
      alertMessage: alertMessageResponses.weWillDeduct
    }
  };
};
const filterOutZeroDrawdowns = (contractBalanceItem) => contractBalanceItem.startingBalance - contractBalanceItem.remainingBalance > 0;
const fillOutstandingInBalanceItem = (contractBalanceItem) => {
  const totalCost = contractBalanceItem.lineItems.reduce(
    (total, lineItem) => total + (lineItem.state === "outstanding" ? lineItem.outstandingAmount : lineItem.netPrice),
    0
  );
  const outstandingBalance = Math.max(
    0,
    totalCost - contractBalanceItem.startingBalance
  );
  assert(
    contractBalanceItem.startingBalance - contractBalanceItem.remainingBalance === totalCost - outstandingBalance,
    `contractBalance item calculation is not verified, contractBalanceItem: ${JSON.stringify(
      contractBalanceItem
    )}, totalCost: ${totalCost}, outstandingBalance: ${outstandingBalance}`
  );
  return { ...contractBalanceItem, outstandingBalance };
};
const fillExhaustAlertInBalanceItem = (contractBalanceItem, _index, contractBalanceItems) => {
  const hasOutstandingAmountLineItem = contractBalanceItem.lineItems.some(
    (lineItem) => lineItem.state === "outstanding"
  );
  if (!hasOutstandingAmountLineItem) {
    return contractBalanceItem;
  }
  const firstContractBalanceItem = contractBalanceItems.find(
    (budgetItem) => budgetItem.key !== contractBalanceItem.key
  );
  assert(
    firstContractBalanceItem,
    "It is not possible to have only one contract to drawdown a paying line item"
  );
  return {
    ...contractBalanceItem,
    ...hasOutstandingAmountLineItem && {
      alertMessage: alertMessageResponses.costTheOutstandingBalance
    }
  };
};
const getAndGroupContractBalanceItems = (lineItems, isUpsell, mapSingleLineItem) => {
  const contractBalanceItems = lineItems.reduce((drawdowns, lineItem) => {
    let contractBalanceItemsToPresent = drawdowns;
    let lineItemPresentation = mapSingleLineItem(lineItem);
    lineItem.drawdownItems.forEach((drawdown) => {
      var _a, _b, _c, _d;
      const key = getKeyFromDrawdown(drawdown);
      const existedDrawdown = drawdowns.find(
        (drawdownItem) => drawdownItem.key === key
      );
      if (existedDrawdown) {
        existedDrawdown.startingBalance = Math.max(
          existedDrawdown.startingBalance,
          ((_b = (_a = drawdown.drawdownContractDetails.currentValue) == null ? void 0 : _a.dollars) == null ? void 0 : _b.value) ?? 0
        );
        existedDrawdown.remainingBalance = Math.min(
          existedDrawdown.remainingBalance,
          ((_d = (_c = drawdown.drawdownContractDetails.remainingValue) == null ? void 0 : _c.dollars) == null ? void 0 : _d.value) ?? 0
        );
        existedDrawdown.lineItems.push(lineItemPresentation);
      } else {
        contractBalanceItemsToPresent = [
          ...contractBalanceItemsToPresent,
          mapDrawdownItem(lineItemPresentation, drawdown, isUpsell)
        ];
      }
      lineItemPresentation = getPayableLineItem(
        lineItemPresentation,
        drawdown.amount,
        drawdown.drawdownContractDetails.contractName ?? drawdown.drawdownContractDetails.contractType
      );
    });
    return contractBalanceItemsToPresent;
  }, []);
  return contractBalanceItems.filter(filterOutZeroDrawdowns).map(fillOutstandingInBalanceItem).map(fillExhaustAlertInBalanceItem);
};
function getPaidAmountAndContractFromLineItems(lineItem) {
  return lineItem.drawdownItems.reduce(
    ([total], drawdown) => [
      total + drawdown.amount,
      drawdown.drawdownContractDetails.contractName ?? drawdown.drawdownContractDetails.contractType
    ],
    [0, ""]
  );
}
const getPayableItems = (lineItems, mapSingleLineItem) => lineItems.map((lineItem) => {
  const lineItemPresentation = mapSingleLineItem(lineItem);
  const [paid, contractName] = getPaidAmountAndContractFromLineItems(lineItem);
  return paid > 0 ? getPayableLineItem(lineItemPresentation, paid, contractName) : lineItemPresentation;
}).filter(
  (lineItem) => lineItem.state === "fullPrice" || lineItem.outstandingAmount !== 0
);
const mapOrderSummaryToOrderSummaryPresentation = (orderSummary, surchargeToApply) => {
  assert(orderSummary != null, "orderSummary is null");
  const amexSurchargeAmount = getAmexSurchargeAmount(
    orderSummary,
    surchargeToApply
  );
  const total = (orderSummary.total.value ?? 0) + amexSurchargeAmount;
  const upsellLineItems = orderSummary.lineItems.filter(
    isUpsellProductLineItem
  );
  const nonUpsellLineItems = orderSummary.lineItems.filter(
    (lineItem) => !isUpsellProductLineItem(lineItem)
  );
  assert(
    upsellLineItems.length <= 1,
    "When upsell, there should be only 1 line item containing upsell product"
  );
  assert(
    upsellLineItems.length === 0 || nonUpsellLineItems.length <= 1,
    "When upsell, there should be at most one Ad in line items"
  );
  assert(
    upsellLineItems.length === 0 || nonUpsellLineItems.filter(isLineItemFullyDrawdown).length === nonUpsellLineItems.length,
    "When upsell, the Ad line items should be fully drawn down"
  );
  const lineItems = upsellLineItems.length > 0 ? upsellLineItems : orderSummary.lineItems;
  const contractBalanceItems = getAndGroupContractBalanceItems(
    upsellLineItems.length > 0 ? nonUpsellLineItems : lineItems,
    upsellLineItems.length > 0,
    mapLineItem
  );
  return {
    tax: orderSummary.tax,
    subTotal: orderSummary.subTotal.value ?? 0,
    total,
    surcharge: amexSurchargeAmount,
    contractBalanceItems,
    payableLineItems: getPayableItems(lineItems, mapLineItem),
    currency: orderSummary.total.currency
  };
};
const mapCPQAOrderSummaryToCPQAOrderSummaryPresentation = (orderSummary, locale) => {
  var _a, _b, _c, _d, _e;
  assert(orderSummary != null, "orderSummary is null");
  const cpqaLineItem = orderSummary.lineItems.find(
    (item) => {
      var _a2;
      return ((_a2 = item.pricing) == null ? void 0 : _a2.__typename) === "CappedUnitPrice";
    }
  );
  assert(
    ((_a = cpqaLineItem == null ? void 0 : cpqaLineItem.pricing) == null ? void 0 : _a.__typename) === "CappedUnitPrice" && ((_b = cpqaLineItem == null ? void 0 : cpqaLineItem.pricing) == null ? void 0 : _b.unitPrice.__typename) === "Price" && ((_c = cpqaLineItem == null ? void 0 : cpqaLineItem.pricing) == null ? void 0 : _c.cappedPrice.__typename) === "Price",
    "lineItem with pricing is not in expected shape"
  );
  const {
    pricing: {
      unitPrice: { netPrice: unitNetPrice, tax: unitPriceTax },
      cappedPrice: { netPrice: cappedNetPrice, tax: cappedPriceTax },
      maxBillableUnits
    }
  } = cpqaLineItem;
  const unitPrice = (unitPriceTax == null ? void 0 : unitPriceTax.displayRecommendation) === "inclusive" ? unitNetPrice.value + (((_d = unitPriceTax.amount) == null ? void 0 : _d.value) ?? 0) : unitNetPrice.value;
  const formattedUnitNetPrice = formatMinorUnits(unitPrice, {
    currency: unitNetPrice.currency,
    locale,
    dropFraction: true
  });
  const formattedCappedNetPrice = formatMinorUnits(cappedNetPrice.value, {
    currency: cappedNetPrice.currency,
    locale
  });
  const formattedCappedTaxPrice = (cappedPriceTax == null ? void 0 : cappedPriceTax.amount) ? formatMinorUnits(cappedPriceTax.amount.value, {
    currency: cappedPriceTax.amount.currency,
    locale
  }) : void 0;
  return {
    maxBillableUnits,
    unitPrice: {
      formattedPrice: formattedUnitNetPrice,
      tax: (unitPriceTax == null ? void 0 : unitPriceTax.displayRecommendation) ? {
        displayRecommendation: unitPriceTax.displayRecommendation,
        code: unitPriceTax.code
      } : void 0
    },
    cappedPrice: {
      formattedPrice: formattedCappedNetPrice,
      tax: cappedPriceTax && formattedCappedTaxPrice ? {
        code: cappedPriceTax.code,
        formattedPrice: formattedCappedTaxPrice
      } : void 0
    },
    total: cappedNetPrice.value + (((_e = cappedPriceTax == null ? void 0 : cappedPriceTax.amount) == null ? void 0 : _e.value) ?? 0),
    currency: cappedNetPrice.currency,
    tax: cappedPriceTax ?? void 0
  };
};
const PayablesSummary = ({
  orderSummary,
  padding,
  locale,
  prefixContent
}) => {
  var _a;
  const { payableLineItems, subTotal, surcharge, tax, total, currency } = orderSummary;
  const { t } = useTranslations(translations$2);
  assert(
    payableLineItems.length > 0,
    "show payable summary only when there is payable line item"
  );
  const isAmountAboveZero = subTotal > 0 || ((_a = tax == null ? void 0 : tax.amount) == null ? void 0 : _a.value) && tax.amount.value > 0 || surcharge > 0;
  const payingProductLineItem = payableLineItems.find(
    (lineItem) => lineItem.state === "outstanding"
  );
  return /* @__PURE__ */ jsx(
    OrderSummaryLayout,
    {
      paddingStyles: padding,
      title: t("order summary..."),
      prefixContent: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
        prefixContent == null ? void 0 : prefixContent.payablesSummary,
        payingProductLineItem && /* @__PURE__ */ jsx(AlertMessage, { message: t(alertMessageResponses.costThisInvoice) })
      ] }),
      children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
        /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
          /* @__PURE__ */ jsx(PayableHeader, {}),
          /* @__PURE__ */ jsx(Divider, { weight: "strong" })
        ] }),
        payableLineItems.map((item) => /* @__PURE__ */ jsx(
          ProductLineItem,
          {
            lineItem: item,
            locale,
            showPartial: true
          },
          item.title
        )),
        isAmountAboveZero && /* @__PURE__ */ jsx(
          SubTotal,
          {
            subTotal,
            tax,
            surcharge,
            locale,
            currency
          }
        ),
        /* @__PURE__ */ jsx(Total, { total, tax, locale, currency })
      ] })
    }
  );
};
const OrderSummary = ({
  trackEventHandler,
  orderSummary,
  orderId,
  padding,
  prefixContent,
  locale,
  space = "xlarge"
}) => {
  const { contractBalanceItems, total } = orderSummary;
  const showContractBalanceItems = (contractBalanceItems == null ? void 0 : contractBalanceItems.length) > 0;
  const showPayablesSummary = total > 0;
  useEffect(() => {
    if (!showPayablesSummary && !showContractBalanceItems) {
      return;
    }
    trackEventHandler(
      "order_summary_displayed",
      {
        orderId
      },
      "view"
    );
  }, [
    orderId,
    trackEventHandler,
    showContractBalanceItems,
    showPayablesSummary
  ]);
  if (!showPayablesSummary && !showContractBalanceItems) {
    return null;
  }
  return /* @__PURE__ */ jsxs(Stack, { space, children: [
    showContractBalanceItems && /* @__PURE__ */ jsx(
      ContractBalance,
      {
        padding,
        prefixContent,
        contractBalanceItems,
        locale
      }
    ),
    showPayablesSummary && /* @__PURE__ */ jsx(
      PayablesSummary,
      {
        orderSummary,
        prefixContent,
        locale
      }
    )
  ] });
};
const HirerOrderSummaryView = (props) => {
  const {
    hideOrderSummary,
    trackEventHandler,
    orderId,
    orderSummary,
    padding,
    space,
    locale,
    prefixContent,
    cpqaOrderSummary
  } = props;
  if (hideOrderSummary) {
    return /* @__PURE__ */ jsx(Bleed, { top: "gutter", children: /* @__PURE__ */ jsx(Fragment, {}) });
  }
  return cpqaOrderSummary ? /* @__PURE__ */ jsx(
    CPQASummary,
    {
      padding,
      locale,
      cpqaOrderSummary,
      trackEventHandler,
      orderId
    }
  ) : /* @__PURE__ */ jsx(
    OrderSummary,
    {
      locale,
      orderId,
      trackEventHandler,
      orderSummary,
      space,
      padding,
      prefixContent
    }
  );
};
const OrderSummaryDataContext = createContext({
  trackEventHandler: () => {
  },
  orderSummary: null,
  styles: {},
  language: "en-AU"
});
const OrderSummaryDataProvider = (props) => {
  var _a;
  const {
    orderId,
    advertiserId,
    surcharge,
    onLoading = () => {
    },
    styles = {},
    children,
    hubbleTrackEventHandler = () => {
    },
    language,
    prefixContent
  } = props;
  const logger = useLogger();
  const {
    loading: orderSummaryLoading,
    data,
    error
  } = useOrderSummaryDetailsQuery({
    variables: { orderId, advertiserId },
    fetchPolicy: "network-only"
  });
  const loadingPreviousRef = useRef();
  useEffect(() => {
    const currentLoading = orderSummaryLoading || !data;
    if (currentLoading !== loadingPreviousRef.current) {
      onLoading(!loadingPreviousRef.current);
      loadingPreviousRef.current = !loadingPreviousRef.current;
    }
  }, [orderSummaryLoading, onLoading, data]);
  const WIDGET_NAME = "OrderSummary";
  const trackEventHandlerWithBaseData = useCallback(
    (eventName, eventData, type = "view") => hubbleTrackEventHandler(
      eventName,
      {
        ...eventData,
        // @ts-expect-error Should probably be added as an optional property on the event data type
        widgetName: WIDGET_NAME
      },
      type
    ),
    [hubbleTrackEventHandler]
  );
  if (error) {
    throw error;
  }
  if (orderSummaryLoading || !data) {
    if (!orderSummaryLoading) {
      logger.error(
        `Warning: OrderSummaryDetails loaded but the data is null: (orderId: ${orderId}, advertiserId: ${advertiserId})`,
        {}
      );
    }
    return /* @__PURE__ */ jsx(OrderSummaryLayout, { paddingStyles: styles.padding, children: /* @__PURE__ */ jsx(Box, { role: "loader", children: /* @__PURE__ */ jsx(LoadingBlock, { size: "small", delayVisibility: true }) }) });
  }
  if (!data.orderSummary) {
    throw new Error(
      `Order (orderId: ${orderId}, advertiserId: ${advertiserId}) does not exist.`
    );
  }
  return /* @__PURE__ */ jsx(
    OrderSummaryDataContext.Provider,
    {
      value: {
        trackEventHandler: trackEventHandlerWithBaseData,
        orderSummary: data.orderSummary,
        surchargeToApply: (surcharge == null ? void 0 : surcharge.surchargeApplicable) ? ((_a = data.orderSummary.surcharges[0]) == null ? void 0 : _a.surchargeType) ?? void 0 : void 0,
        styles,
        language,
        prefixContent
      },
      children
    }
  );
};
const useOrderSummaryPresentationData = () => {
  const {
    orderSummary,
    surchargeToApply,
    styles: { padding, space },
    language,
    prefixContent,
    trackEventHandler
  } = useContext(OrderSummaryDataContext);
  assert(
    orderSummary,
    "orderSummary must be defined in the OrderSummaryDataProvider"
  );
  const orderSummaryPresentation = mapOrderSummaryToOrderSummaryPresentation(
    orderSummary,
    surchargeToApply
  );
  const isCPQASummary = Boolean(
    orderSummary.lineItems.find(
      (item) => {
        var _a;
        return ((_a = item.pricing) == null ? void 0 : _a.__typename) === "CappedUnitPrice";
      }
    )
  );
  const cpqaSummaryPresentation = isCPQASummary ? mapCPQAOrderSummaryToCPQAOrderSummaryPresentation(orderSummary, language) : void 0;
  return {
    orderId: orderSummary.orderId,
    orderSummary: orderSummaryPresentation,
    padding,
    space,
    language,
    cpqaOrderSummary: cpqaSummaryPresentation,
    prefixContent,
    trackEventHandler
  };
};
const HirerOrderSummaryContainer = () => {
  const {
    orderId,
    orderSummary,
    padding,
    space,
    language,
    cpqaOrderSummary,
    prefixContent,
    trackEventHandler
  } = useOrderSummaryPresentationData();
  return /* @__PURE__ */ jsx(
    HirerOrderSummaryView,
    {
      trackEventHandler,
      orderId,
      orderSummary,
      padding,
      space,
      locale: language,
      cpqaOrderSummary,
      prefixContent
    }
  );
};
const HirerOrderSummary = (props) => /* @__PURE__ */ jsx(LoggerProvider, { context: props.logger || {}, children: /* @__PURE__ */ jsx(OrderSummaryDataProvider, { ...props, children: /* @__PURE__ */ jsx(HirerOrderSummaryContainer, {}) }) });
const View = ({
  loading,
  error,
  expected,
  errorComponent
}) => {
  if (loading) {
    return /* @__PURE__ */ jsx(Box, { role: "loading-block", children: /* @__PURE__ */ jsx(LoadingBlock, {}) });
  }
  if (error) {
    return errorComponent ? errorComponent : /* @__PURE__ */ jsxs(Box, { role: "error-alert", children: [
      /* @__PURE__ */ jsx(ErrorAlert, {}),
      ";"
    ] });
  }
  return expected();
};
const getCheckoutPaymentResult = ({
  paymentResult,
  orderNumber: inputOrderNumber,
  promoCode,
  totalOrderAmountPayable,
  paymentMethod
}) => {
  const orderNumber = inputOrderNumber ?? "";
  return {
    ...paymentResult,
    paymentMethod: paymentResult.paymentMethod ?? paymentMethod,
    productTotalPrice: totalOrderAmountPayable ?? 0,
    discountCode: promoCode ?? "",
    orderNumber
  };
};
const calculateTotalOrderAmountPayable = (orderSummary) => {
  if (!orderSummary) {
    return void 0;
  }
  return orderSummary.lineItems.reduce(
    (acc, current) => acc + current.price.netPrice.value,
    0
  );
};
const isBillToAgent = (hirerAccount) => {
  if (!hirerAccount) {
    return false;
  }
  return (hirerAccount.hasAgent ?? false) && hirerAccount.billingStatus === "OK";
};
const isInvoiceOnly = (contractBalances) => (contractBalances == null ? void 0 : contractBalances.length) > 0;
const isPayingWithBudget = (contractBalances, totalOrderAmountPayable) => {
  if (totalOrderAmountPayable === void 0 || contractBalances === void 0) {
    return false;
  }
  const totalRemaining = contractBalances.reduce(
    (acc, contract) => acc + contract.totalValue.dollars.remaining.value,
    0
  );
  return totalRemaining >= totalOrderAmountPayable;
};
const isPaymentDetailsHidden = (payingWithBudget, totalOrderAmountPayable, hidePrice, isPerUnitBilling2 = false) => {
  const isFreeProduct = Boolean(Number(totalOrderAmountPayable) === 0);
  if (isPerUnitBilling2) {
    return false;
  }
  return Boolean(hidePrice || isFreeProduct || payingWithBudget);
};
const isPaymentRequired = (totalOrderAmountPayable, payingWithBudget) => Number(totalOrderAmountPayable) > 0 && !payingWithBudget;
const isPerUnitBilling = (orderSummary) => {
  var _a;
  const result = (_a = orderSummary == null ? void 0 : orderSummary.lineItems) == null ? void 0 : _a.find(
    (item) => {
      var _a2;
      return ((_a2 = item.pricing) == null ? void 0 : _a2.__typename) === "CappedUnitPrice";
    }
  );
  return Boolean(result);
};
const getPaymentFieldsFromData = ({
  contractBalances,
  hirerAccount,
  orderSummary
}) => {
  const billToAgent = isBillToAgent(hirerAccount);
  const invoiceOnly = isInvoiceOnly(contractBalances);
  const totalOrderAmountPayable = calculateTotalOrderAmountPayable(orderSummary);
  const payingWithBudget = isPayingWithBudget(
    contractBalances,
    totalOrderAmountPayable
  );
  const hideAllPayments = isPaymentDetailsHidden(
    payingWithBudget,
    totalOrderAmountPayable,
    (hirerAccount == null ? void 0 : hirerAccount.hidePrice) ?? false,
    isPerUnitBilling(orderSummary)
  );
  const requiresPayment = isPaymentRequired(
    totalOrderAmountPayable,
    payingWithBudget
  );
  return {
    billToAgent,
    hideAllPayments,
    invoiceOnly,
    payingWithBudget,
    requiresPayment,
    totalOrderAmountPayable
  };
};
const useShoppingCart = () => {
  const shoppingCartContext = useContext(ShoppingCartContext);
  if (!shoppingCartContext) {
    throw new Error("ShoppingCartContext is not provided");
  }
  if (!shoppingCartContext.cartState) {
    throw new Error("Cart is not loaded");
  }
  return {
    ...shoppingCartContext,
    cartState: shoppingCartContext.cartState
  };
};
const isAdBudgetSelected = (cartState) => {
  var _a;
  const result = (_a = cartState.items) == null ? void 0 : _a.find(
    (item) => item.product.__typename === "AdBudgetCartItem"
  );
  return Boolean(result);
};
const isOrderExpiredError = (error) => {
  const isKnownError = "errorCode" in error;
  if (!isKnownError) {
    return false;
  }
  const errorCode = error.errorCode;
  return errorCode === "order_expired";
};
const DEFAULT_PAYMENT_METHOD = "CreditCard";
const PaymentsController = ({
  callbacks,
  onGetSurchargeInfo,
  backButtonComponent,
  env,
  idempotencyKey,
  logger,
  orderId,
  postAdButtonText,
  previewButtonComponent,
  site,
  useCase,
  hirerAccount,
  orderSummary,
  contractBalances
}) => {
  const { cartState, createOrder, orderState } = useShoppingCart();
  const isAdBudget = isAdBudgetSelected(cartState);
  const {
    billToAgent,
    hideAllPayments,
    invoiceOnly,
    payingWithBudget,
    requiresPayment,
    totalOrderAmountPayable
  } = getPaymentFieldsFromData({
    contractBalances,
    hirerAccount,
    orderSummary
  });
  const determineInitialPaymentMethod = () => invoiceOnly || hideAllPayments ? "Invoice" : DEFAULT_PAYMENT_METHOD;
  const [paymentMethodSelected, setPaymentMethodSelected] = useState(determineInitialPaymentMethod());
  const checkoutOnPaymentMethodChanged = (_selectedIndex, selectedItem) => {
    if (!selectedItem) {
      return;
    }
    setPaymentMethodSelected(selectedItem);
    if (callbacks.onPaymentMethodChanged) {
      callbacks.onPaymentMethodChanged(_selectedIndex, selectedItem);
    }
  };
  const checkoutOnPaymentFailed = (error) => {
    if (isOrderExpiredError(error)) {
      logger.warn("Checkout | PaymentsController | Order expired", {
        err: error
      });
      createOrder();
    } else {
      logger.error("Checkout | PaymentsController | onPaymentFailed", {});
      return callbacks.onPaymentFailed(error);
    }
  };
  const checkoutOnPaymentSuccess = (result) => {
    var _a;
    if (orderState.order === void 0) {
      logger.warn(
        "Checkout | PaymentsController | onPaymentSuccess | Order not found",
        {
          cart: cartState,
          order: orderState
        }
      );
    }
    if (totalOrderAmountPayable === void 0) {
      logger.warn(
        "Checkout | PaymentsController | onPaymentSuccess | totalOrderAmountPayable not found",
        {
          cart: cartState,
          order: orderState
        }
      );
    }
    const checkoutPaymentResult = getCheckoutPaymentResult({
      paymentResult: result,
      orderNumber: (_a = orderState.order) == null ? void 0 : _a.orderNumber,
      promoCode: cartState.promoCode,
      totalOrderAmountPayable,
      paymentMethod: paymentMethodSelected
    });
    return callbacks.onPaymentSuccess(checkoutPaymentResult);
  };
  const trackLink = (eventName, eventAttributes) => {
    callbacks.trackEventHandler(eventName, eventAttributes, "link");
  };
  useEffect(() => {
    if (hirerAccount.nation && contractBalances && orderSummary) {
      callbacks.trackEventHandler(
        "checkout_widget",
        {
          paymentMethodShown: hideAllPayments ? "not shown" : getSupportedPaymentMethodsForCaja(
            hirerAccount.nation.toLowerCase()
          ),
          hasSufficientBudget: payingWithBudget,
          requiresPayment
        },
        "link"
      );
    }
  }, [contractBalances, orderSummary, hirerAccount.nation]);
  return /* @__PURE__ */ jsx(
    PaymentsForCaja,
    {
      backButtonComponent,
      billToAgent,
      cartId: cartState.id,
      currency: orderSummary.total.currency,
      env,
      handledCancelPayment: callbacks.handleCancelledPayment,
      hideAllPayments,
      idempotencyKey,
      invoiceOnly,
      isAdBudgetSelected: isAdBudget,
      onError: checkoutOnPaymentFailed,
      onPaymentMethodChanged: checkoutOnPaymentMethodChanged,
      onPostAdButtonClicked: callbacks.onCheckoutButtonClicked,
      onPostAdButtonClickFailed: callbacks.onCheckoutButtonClickedFailed,
      onReplaceSavedCard: callbacks.onReplaceSavedCard,
      onSaveCardOptionChanged: callbacks.onSaveCardOptionChanged,
      onSavedCardLoaded: callbacks.onSavedCardLoaded,
      onSuccess: checkoutOnPaymentSuccess,
      orderId,
      paymentMethod: paymentMethodSelected,
      postAdButtonText,
      previewButtonComponent,
      site,
      trackLink,
      useCase,
      onGetSurchargeInfo
    }
  );
};
const getProductIdForPromoCode = (cartItems, logger) => {
  if (cartItems.length === 0) {
    logger.error(
      "Cart items are empty, using empty string as productId for HirerPromoCodesInput",
      {}
    );
    return "";
  }
  const ROOT_PRODUCT_REFERENCE_ID = "product|parent";
  const parentItem = cartItems.find(
    (item) => item.referenceId === ROOT_PRODUCT_REFERENCE_ID
  );
  if (!parentItem) {
    return cartItems[0].product.id;
  }
  return parentItem.product.id;
};
const PromoCodeBanner = ({
  cartState,
  logger
}) => {
  const selectedProductId = getProductIdForPromoCode(cartState.items, logger);
  return /* @__PURE__ */ jsx(
    HirerPromoCodesBanner,
    {
      productIds: [selectedProductId],
      padding: { paddingX: "medium", paddingY: "medium" }
    }
  );
};
const isPromoCodeInputShown = ({
  cartItems,
  totalOrderAmountPayable,
  cartPromoCode
}) => {
  const selectedAdBudget = cartItems.find(
    (item) => item.product.__typename === "AdBudgetCartItem"
  );
  const promoCodeSuccessfullyApplied = cartPromoCode !== void 0;
  const isPaymentRequired2 = Number(totalOrderAmountPayable) > 0;
  const canApplyPromoCode = isPaymentRequired2 || promoCodeSuccessfullyApplied;
  return !selectedAdBudget && canApplyPromoCode;
};
const PromoCodeController = (props) => {
  const { orderSummary, logger, trackEventHandler } = props;
  const { cartState, onApplyPromoCode, onClearPromoCode } = useShoppingCart();
  const productId = getProductIdForPromoCode(cartState.items, props.logger);
  const { shouldRender: shouldRenderPromoCodesBanner } = usePromoCodesBanner([
    productId
  ]);
  const totalOrderAmountPayable = calculateTotalOrderAmountPayable(orderSummary);
  if (!isPromoCodeInputShown({
    cartItems: cartState.items,
    totalOrderAmountPayable,
    cartPromoCode: cartState.promoCode ?? void 0
  })) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    HirerPromoCodesInput,
    {
      promoCodeFromParent: cartState.promoCode ?? "",
      productId,
      bannerToDisplay: shouldRenderPromoCodesBanner ? /* @__PURE__ */ jsx(PromoCodeBanner, { cartState, logger }) : void 0,
      onSuccess: (promoCode) => {
        onApplyPromoCode(promoCode);
        trackEventHandler(
          "apply_promo_code_succeeded",
          {
            eventType: "apply_promo_code_succeeded",
            currentPageSection: "promo code",
            discountCode: promoCode
          },
          "link"
        );
      },
      onClear: () => {
        onClearPromoCode();
      },
      onError: (error) => trackEventHandler(
        "form_validation_error_displayed",
        {
          eventType: "apply_promo_code_failed",
          currentPageSection: "promo code",
          errorMessage: error.message ?? "Error applying promo code",
          discountCode: cartState.promoCode ?? "",
          validationType: "field"
        },
        "link"
      ),
      onInvalid: (_message, discountCode) => trackEventHandler(
        "form_validation_error_displayed",
        {
          eventType: "apply_promo_code_failed",
          currentPageSection: "promo code",
          errorMessage: "Error applying promo code",
          discountCode,
          validationType: "field"
        },
        "link"
      ),
      onClick: (discountCode) => trackEventHandler(
        "apply_promo_code_pressed",
        {
          eventType: "apply_promo_code_pressed",
          currentPageSection: "promo code",
          discountCode
        },
        "link"
      )
    }
  );
};
const useShoppingCartContext = () => {
  const shoppingCartContext = useContext(ShoppingCartContext);
  if (!shoppingCartContext) {
    throw new Error("ShoppingCartContext is not provided");
  }
  return shoppingCartContext;
};
const useCheckoutData = (locale, logger, notifyFatalError) => {
  var _a, _b, _c;
  const { createOrder, orderState, cartState } = useShoppingCartContext();
  const orderId = (_a = orderState.order) == null ? void 0 : _a.orderId;
  const [surcharge, setSurcharge] = useState(void 0);
  const onGetSurchargeInfo = useCallback(
    (surchargeBrand, surchargePercentage, surchargeApplicable) => {
      setSurcharge({
        surchargeBrand,
        surchargePercentage,
        surchargeApplicable
      });
    },
    [setSurcharge]
  );
  const {
    loading: orderSummaryLoading,
    data,
    error
  } = useGetOrderSummaryQuery({
    variables: { orderId: orderId ?? "" },
    fetchPolicy: "cache-first",
    skip: !orderId
  });
  if (error) {
    logger.error("Checkout | OrderSummaryDetailsQuery", { error });
  }
  const orderRef = useRef(orderState.order);
  const loading = !cartState || orderState.loading || orderSummaryLoading || orderId === void 0;
  const fatalError = {
    createOrderError: orderState.createOrderFatalError,
    cartNotReady: !(cartState == null ? void 0 : cartState.isOrderReady)
  };
  const cartStateStringify = JSON.stringify(cartState);
  useEffect(() => {
    if (!cartStateStringify) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const isReturningLinkoutPaymentWithOrderId = typeof params.get("orderid") === "string";
    if (isReturningLinkoutPaymentWithOrderId) {
      return;
    }
    createOrder();
  }, [cartStateStringify, createOrder]);
  useEffect(() => {
    if (orderState.productAlert === "SELECTED_PRODUCT_UNAVAILABLE") {
      logger.warn("Selected Product Unavailable", { cartState });
      notifyFatalError({
        reason: "product_unavailable"
      });
    }
  }, [orderState.productAlert]);
  useEffect(() => {
    if (fatalError == null ? void 0 : fatalError.createOrderError) {
      logger.error("Create Order Fatal Error", {
        message: fatalError.createOrderError
      });
      notifyFatalError({
        reason: "create_order_fatal_error"
      });
    }
  }, [fatalError.createOrderError]);
  useEffect(() => {
    orderRef.current = orderState.order;
  }, [orderState.order]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (orderRef.current === void 0) {
        logger.info(
          "No order found, retriggering create order after 5 seconds",
          { orderState }
        );
        createOrder();
      }
    }, 5e3);
    return () => clearTimeout(timer);
  }, []);
  const orderSummary = data == null ? void 0 : data.orderSummary;
  const surchargeToApply = (surcharge == null ? void 0 : surcharge.surchargeApplicable) ? ((_c = (_b = data == null ? void 0 : data.orderSummary) == null ? void 0 : _b.surcharges[0]) == null ? void 0 : _c.surchargeType) ?? void 0 : void 0;
  const orderSummaryPresentation = Boolean(orderSummary) ? mapOrderSummaryToOrderSummaryPresentation(orderSummary, surchargeToApply) : void 0;
  const isCPQASummary = orderSummary && Boolean(
    orderSummary == null ? void 0 : orderSummary.lineItems.find(
      (item) => {
        var _a2;
        return ((_a2 = item.pricing) == null ? void 0 : _a2.__typename) === "CappedUnitPrice";
      }
    )
  );
  const cpqaSummaryPresentation = isCPQASummary ? mapCPQAOrderSummaryToCPQAOrderSummaryPresentation(orderSummary, locale) : void 0;
  const includePayablesSummary = orderState.productAlert === "SELECTED_PRODUCT_PRICE_CHANGED";
  return {
    orderId,
    loading,
    fatalError,
    orderSummaryDetails: orderSummary,
    orderSummary: orderSummaryPresentation,
    cpqaOrderSummary: cpqaSummaryPresentation,
    includePayablesSummary,
    onGetSurchargeInfo
  };
};
const useContractBalances = (logger) => {
  const { data, loading } = useGetContractBalancesQuery({
    variables: {
      advertiserNation: "test-advertiser-nation"
      // TODO: needs removal
    },
    onError: (error) => {
      logger.error("Checkout | GetContractBalancesQuery", {
        error
      });
    },
    fetchPolicy: "network-only"
  });
  return {
    loading,
    contractBalances: data == null ? void 0 : data.contractBalances
  };
};
const useHirerAccount = (logger) => {
  const { data, loading } = useHirerAccountQuery({
    onError: (error) => {
      logger.error("Checkout | HirerAccountQuery", {
        error
      });
    }
  });
  return {
    loading,
    hirerAccount: data == null ? void 0 : data.hirerAccount
  };
};
const defaultLocale = "en-AU";
const useLocale = () => {
  const { locale } = useLanguage();
  if (!locale) {
    return defaultLocale;
  }
  const isMelwaysLocale = (input) => locales.includes(locale);
  return isMelwaysLocale() ? locale : defaultLocale;
};
const CreateOrderError = () => {
  const { t } = useTranslations(translations$3);
  return /* @__PURE__ */ jsx(Alert, { tone: "critical", children: /* @__PURE__ */ jsxs(Text, { children: [
    /* @__PURE__ */ jsxs("b", { children: [
      " ",
      t("checkout-page-error-title")
    ] }),
    /* @__PURE__ */ jsx("p", { children: t("checkout-page-error-text-content") })
  ] }) });
};
const PriceChangeAlert = () => {
  const { t } = useTranslations(translations$3);
  return /* @__PURE__ */ jsx(Alert, { tone: "info", children: /* @__PURE__ */ jsx(Text, { children: t("price-changed-notice-text-content") }) });
};
const defaultLogger = {
  /* eslint-disable no-console */
  warn: console.warn,
  error: console.error,
  info: console.info
  /* eslint-enable no-console */
};
const Checkout = (props) => {
  const logger = props.logger ?? defaultLogger;
  const locale = useLocale();
  const theme = useThemeName();
  const { loading: contractBalancesLoading, contractBalances } = useContractBalances(logger);
  const { hirerAccount, loading: hirerLoading } = useHirerAccount(logger);
  const {
    orderId,
    loading: checkoutDataLoading,
    fatalError,
    orderSummary,
    cpqaOrderSummary,
    includePayablesSummary,
    onGetSurchargeInfo,
    orderSummaryDetails
  } = useCheckoutData(locale, logger, props.callbacks.notifyFatalError);
  const trackEventHandler = (eventName, eventData, type = "view") => {
    props.callbacks.onTrackingEvent({
      tracking: {
        eventName,
        eventData,
        type
      },
      hubble: {
        eventName,
        eventData
      }
    });
  };
  const checkoutLoading = checkoutDataLoading || contractBalancesLoading || hirerLoading || !Boolean(orderId) || (fatalError == null ? void 0 : fatalError.cartNotReady) || orderSummaryDetails === void 0;
  const hideOrderSummary = (hirerAccount == null ? void 0 : hirerAccount.hidePrice) === true;
  const ExpectedCheckout = () => /* @__PURE__ */ jsx(ContentBlock, { width: "medium", children: /* @__PURE__ */ jsxs(Stack, { space: "gutter", children: [
    /* @__PURE__ */ jsx(
      PromoCodeController,
      {
        trackEventHandler,
        logger,
        orderSummary: orderSummaryDetails
      }
    ),
    /* @__PURE__ */ jsx(
      HirerOrderSummaryView,
      {
        trackEventHandler,
        locale,
        orderId,
        prefixContent: {
          payablesSummary: includePayablesSummary ? /* @__PURE__ */ jsx(PriceChangeAlert, {}) : void 0
        },
        padding: {
          paddingTop: theme === "apac" ? "large" : "none",
          paddingBottom: theme === "apac" ? "large" : "none",
          paddingX: theme === "apac" ? "gutter" : void 0
        },
        cpqaOrderSummary,
        orderSummary,
        space: "medium",
        hideOrderSummary
      }
    ),
    /* @__PURE__ */ jsx(
      PaymentsController,
      {
        callbacks: {
          handleCancelledPayment: props.callbacks.handleCancelledPayment,
          onCheckoutButtonClicked: props.callbacks.onPostAdButtonClick,
          onCheckoutButtonClickedFailed: props.callbacks.onPostAdButtonClickFailed,
          onPaymentFailed: props.callbacks.onPaymentFailed,
          onPaymentSuccess: props.callbacks.onPaymentSuccess,
          onPaymentMethodChanged: props.callbacks.handlePaymentOptionChange,
          onReplaceSavedCard: props.callbacks.onReplaceSavedCard,
          onSaveCardOptionChanged: props.callbacks.onSaveCardOptionChanged,
          onSavedCardLoaded: props.callbacks.onSavedCardLoaded,
          trackEventHandler
        },
        backButtonComponent: props.backButtonComponent,
        env: props.env,
        idempotencyKey: props.idempotencyKey,
        logger,
        postAdButtonText: props.postAdButtonText,
        previewButtonComponent: props.previewButtonComponent,
        site: props.site,
        useCase: props.useCase,
        onGetSurchargeInfo,
        orderId,
        hirerAccount,
        orderSummary: orderSummaryDetails,
        contractBalances
      }
    )
  ] }) });
  return /* @__PURE__ */ jsx(
    View,
    {
      loading: checkoutLoading,
      error: Boolean(fatalError == null ? void 0 : fatalError.createOrderError),
      errorComponent: /* @__PURE__ */ jsx(CreateOrderError, {}),
      expected: ExpectedCheckout
    }
  );
};
export {
  Checkout,
  HirerContractBalance,
  HirerOrderSummary,
  Locales as Locale,
  checkIsPaymentsReturnUrl
};
